import { computed, WritableComputedRef, type Ref } from 'vue';
import { useRoute, useRouter, type LocationQueryRaw, type RouteLocationRaw } from 'vue-router';

type SetMode = 'push' | 'replace';
type Maybe<T> = T | null | undefined;

function normalizeQueryObject(query: Record<string, unknown>): Record<string, string | string[]> {
    const normalized: Record<string, string | string[]> = {};
    for (const [key, rawValue] of Object.entries(query)) {
        if (rawValue == null) continue;
        if (Array.isArray(rawValue)) {
            normalized[key] = rawValue.map(String);
        } else {
            normalized[key] = String(rawValue);
        }
    }
    return normalized;
}

function buildSearchParams(query: Record<string, unknown>): URLSearchParams {
    const searchParams = new URLSearchParams();
    for (const [key, rawValue] of Object.entries(query)) {
        if (rawValue == null) continue;
        if (Array.isArray(rawValue)) {
            for (const item of rawValue) searchParams.append(key, String(item));
        } else {
            searchParams.set(key, String(rawValue));
        }
    }
    return searchParams;
}

function toLocationQueryRaw(input: Record<string, unknown>): LocationQueryRaw {
    const output: LocationQueryRaw = {};
    for (const [key, rawValue] of Object.entries(input)) {
        if (rawValue == null || rawValue === '') continue;
        if (Array.isArray(rawValue)) {
            output[key] = rawValue.map((item) => String(item));
        } else if (
            typeof rawValue === 'string' ||
            typeof rawValue === 'number' ||
            typeof rawValue === 'boolean'
        ) {
            output[key] = String(rawValue);
        } else {
            output[key] = String(rawValue as any);
        }
    }
    return output;
}

export function useQueryString() {
    const route = useRoute();
    const router = useRouter();

    const params = computed<URLSearchParams>(() =>
        buildSearchParams(route.query as Record<string, unknown>)
    ) as unknown as Ref<URLSearchParams>;

    const get = (key: string, fallbackValue?: string): string => {
        const rawValue = route.query[key];
        if (rawValue == null) return fallbackValue ?? '';
        return Array.isArray(rawValue)
            ? String(rawValue[0] ?? fallbackValue ?? '')
            : String(rawValue);
    };

    const getAll = (key: string): string[] => {
        const rawValue = route.query[key];
        if (rawValue == null) return [];
        return Array.isArray(rawValue) ? rawValue.map(String) : [String(rawValue)];
    };

    const asObject = computed(() => normalizeQueryObject(route.query as Record<string, unknown>));

    const navigateWithQuery = async (nextQuery: Record<string, unknown>, updateMode: SetMode) => {
        const cleanedQuery = toLocationQueryRaw(nextQuery);

        const targetLocation: RouteLocationRaw = {
            name: route.name ?? undefined,
            params: route.params,
            hash: route.hash,
            query: cleanedQuery,
        };

        return updateMode === 'replace'
            ? router.replace(targetLocation)
            : router.push(targetLocation);
    };

    const set = (
        key: string,
        value: Maybe<string | number | boolean>,
        updateMode: SetMode = 'push'
    ) => {
        const updatedQuery = { ...(route.query as Record<string, unknown>) };
        if (value === '' || value == null) {
            delete updatedQuery[key];
        } else {
            updatedQuery[key] = String(value);
        }
        return navigateWithQuery(updatedQuery, updateMode);
    };

    const append = (
        key: string,
        value: string | number | boolean,
        updateMode: SetMode = 'push'
    ) => {
        const updatedQuery = { ...(route.query as Record<string, unknown>) };
        const existingValue = updatedQuery[key];
        const stringified = String(value);

        if (existingValue == null) {
            updatedQuery[key] = [stringified];
        } else if (Array.isArray(existingValue)) {
            updatedQuery[key] = [...existingValue.map(String), stringified];
        } else {
            updatedQuery[key] = [String(existingValue), stringified];
        }

        return navigateWithQuery(updatedQuery, updateMode);
    };

    const setMany = (patch: Record<string, unknown>, updateMode: SetMode = 'replace') => {
        const updated: Record<string, unknown> = { ...(route.query as Record<string, unknown>) };

        for (const [key, raw] of Object.entries(patch)) {
            if (raw === '' || raw == null || (Array.isArray(raw) && raw.length === 0)) {
                delete updated[key];
            } else {
                updated[key] = raw;
            }
        }
        return navigateWithQuery(updated, updateMode);
    };

    const remove = (key: string, updateMode: SetMode = 'push') => {
        const updatedQuery = { ...(route.query as Record<string, unknown>) };
        delete updatedQuery[key];
        return navigateWithQuery(updatedQuery, updateMode);
    };

    return {
        params,
        asObject,
        get,
        getAll,
        set,
        setMany,
        append,
        remove,
    };
}

export function useQueryParam(
    key: string,
    options?: {
        defaultValue?: string | null;
        updateMode?: SetMode;
    }
) {
    const route = useRoute();
    const { defaultValue = null, updateMode = 'push' } = options ?? {};
    const { set } = useQueryString();

    const value = computed<string | null>({
        get: () => {
            const rawValue = route.query[key];
            if (rawValue == null) return defaultValue;
            return Array.isArray(rawValue) ? (rawValue[0] ?? defaultValue) : String(rawValue);
        },
        set: (newValue) => {
            set(key, newValue, updateMode);
        },
    });

    const clear = (modeOverride: SetMode = updateMode) => set(key, null, modeOverride);

    const has = computed(() => Object.prototype.hasOwnProperty.call(route.query, key));

    return {
        value,
        set: (newValue: Maybe<string>) => set(key, newValue, updateMode),
        clear,
        has,
    };
}

export function useNumericQueryParam(
    key: string,
    options?: {
        startIndex?: number;
        updateMode?: 'push' | 'replace';
        oneBased?: boolean; // om du vill visa 1 i URL men använda 0 internt
    }
): WritableComputedRef<number> {
    const { startIndex = 0, updateMode = 'push', oneBased = false } = options ?? {};
    const { value: rawValue } = useQueryParam(key, {
        defaultValue: String(startIndex),
        updateMode,
    });

    return computed<number>({
        get: () => {
            const n = Number(rawValue.value);
            if (!Number.isFinite(n)) return startIndex;
            return oneBased ? Math.max(1, n) - 1 : n;
        },
        set: (next: number) => {
            const safe = Number.isFinite(next) ? next : startIndex;
            rawValue.value = String(oneBased ? safe + 1 : safe);
        },
    });
}
